import { lazy, Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { STRINGS } from '../../Constants/ConstantStrings';
import NoDataFound from '../../SharedComponets/NoDataFound';
import {
  deleteQuestion,
  getclosureCategories,
  requestQuestionsLibraryData
} from '../../Store/reducers/ClosureQuestions';
import { useReduxDispatch, useReduxSelector } from '../../Store/reduxHooks';
import {
  EmptyQuestionsListIcon,
  NoQuestionsIcon
} from '../../Universal/Assets';
import { colorState } from '../../Universal/Foundation';
import { Button, Icon } from '../../Universal/NovusDSImports';
import {
  btnStyles,
  confirmPopupModalStyles
} from '../../Universal/NovusDSImports/variants';
import { TooltipComponent } from '../../SharedComponets/Tooltip';
import { RootState } from '../../store';
import ConfirmationPopupModal from '../../SharedComponets/ConfirmationPopupModal';
import { closureQuestionsReceived } from '../../Store/reducers/PublicView';
import { defaultGetResponse } from '../../GetResponse';
import usePrevious from '../../Hooks/UsePrevious';
import PALoader from '../../SharedComponets/PALoader';
import {
  getOrderingParam,
  getPermission
} from '../../CommonUtilities/CommonUtilities';
import {
  setShowTableLoader,
  unSelectSelectedRows
} from '../../Store/reducers/Common';
import { questionsLibraryTableRowLimit } from '../../Constants/constants';

const GenericTableRT7 = lazy(
  () => import('../GenericTableComponent/GenericTable')
);

export const EventQuestionLibraryTable = (props: any) => {
  const {
    selectedCategory,
    activeTab,
    setIsEdit,
    setEditData,
    setIsOpenModal,
    setSelectedRows,
    selectedRows,
    addQuestion,
    isQuestionLibrary
  } = props;
  const dispatch = useReduxDispatch(),
    [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false),
    [deleteData, setDeleteData] = useState<any>(),
    [isLoadingInitialData, setIsLoadingInitialData] = useState<boolean>(true),
    [isSelectAll, setIsSelectAll] = useState<boolean>(false),
    questionslibraryData = useSelector(
      (state: RootState) => state.Questions.questionslibraryData
    ),
    currentUser = useReduxSelector(
      (state: RootState) => state.Common.currentUserDetails
    ),
    isFetchingQuestionsLibraryData = useSelector(
      (state: RootState) => state.Questions.isFetchingQuestionsLibraryData
    ),
    shouldUnselect = useReduxSelector(
      (state: RootState) => state.Common.isMobileScreen
    ),
    selectedClosure = useReduxSelector(
      (state: RootState) => state.Common.selectedClosure
    ),
    categories = useSelector((state: any) => state.Questions.categories),
    previousIsFetchingQuestionsLibraryData = usePrevious(
      isFetchingQuestionsLibraryData
    );
  const isAdmin = useReduxSelector(
    (state: RootState) =>
      state?.Common?.currentUserDetails?.user_permission_set?.role_names
  )?.includes('Admin');

  const [tableSortOrder, setTableSortOrder] = useState<
    Array<{ id: string; desc: boolean }>
  >([]);

  useEffect(() => {
    if (
      previousIsFetchingQuestionsLibraryData &&
      !isFetchingQuestionsLibraryData &&
      isLoadingInitialData
    ) {
      setIsLoadingInitialData(false);
    }
  }, [
    isFetchingQuestionsLibraryData,
    isLoadingInitialData,
    previousIsFetchingQuestionsLibraryData
  ]);

  useEffect(() => {
    if (shouldUnselect) {
      setSelectedRows({});
      setIsSelectAll(false);
    }
  }, [dispatch, shouldUnselect, setSelectedRows]);

  const editQuestion = (data: any) => {
    setIsOpenModal(true);
    setIsEdit(true);
    setEditData(data);
  };

  const deleteLibraryQuestion = (data: any) => {
    setDeleteData(data);
    setIsDeleteModalOpen(true);
  };

  useEffect(() => {
    return () => {
      dispatch(closureQuestionsReceived({ data: defaultGetResponse }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (isQuestionLibrary) {
      dispatch(getclosureCategories({ limit: 'all' }));
    }
  }, [dispatch, isQuestionLibrary]);

  const columns = [
    {
      Header: 'Questions',
      accessor: 'display_name',
      Cell: ({ row }) => (
        <section
          className="event-questions-wrapper"
          aria-label={`
            
          Question : ${row.original.display_name}

          ${row.original.default ? ' Default Question.' : ''}

          ${
            row.original.multiple_choices?.length
              ? ' Choices are ' +
                row.original.multiple_choices
                  .map((item: any) => item)
                  .join(', ')
              : ''
          }
          
          `}
        >
          {row.original.display_name}
          {row.original.default && ' *'}
          {row.original.multiple_choices?.length ? (
            <ul>
              {row.original.multiple_choices.map((item: any, index: number) => {
                return <li key={index}>{item}</li>;
              })}
            </ul>
          ) : (
            <div>{row.original.numeric}</div>
          )}
        </section>
      ),
      label: ({ row }) => `
            
          Question : ${row.original.display_name}

          ${row.original.default ? ' Default Question.' : ''}

          ${
            row.original.multiple_choices?.length
              ? ' Choices are ' +
                row.original.multiple_choices
                  .map((item: any) => item)
                  .join(', ')
              : ''
          }
          
          `
    },
    {
      Header: STRINGS.TYPE,
      Cell: ({ row }) => (
        <section
          className="text-capitalize"
          aria-label={
            STRINGS.TYPE +
            ' : ' +
            (row.original.field_type === 'numeric'
              ? STRINGS.NUMERIC
              : STRINGS.CHOICE)
          }
        >
          {row.original.field_type === 'numeric'
            ? STRINGS.NUMERIC
            : STRINGS.CHOICE}
        </section>
      ),
      label: ({ row }) =>
        STRINGS.TYPE +
        ' : ' +
        (row.original.field_type === 'numeric'
          ? STRINGS.NUMERIC
          : STRINGS.CHOICE)
    },
    {
      Header: STRINGS.ABBREVIATED,
      accessor: 'abbr_name',
      Cell: ({ row }) => (
        <section
          aria-label={STRINGS.ABBREVIATED + ' : ' + row.original.abbr_name}
        >
          {row.original.abbr_name}
        </section>
      ),
      label: ({ row }) => STRINGS.ABBREVIATED + ' : ' + row.original.abbr_name
    },
    {
      Header: STRINGS.ACTIONS,
      Cell: ({ row }) => (
        <section className="d-flex" aria-label="action-buttons">
          <span className="visually-hidden">{STRINGS.ACTIONS}</span>
          {getPermission(currentUser, 'change_closureticket') && (
            <div
              role="button"
              aria-label={`delete question ${row.original.display_name}`}
              data-testid="delete-question-action"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  if (isAdmin) {
                    deleteLibraryQuestion(row.original);
                  }
                }
              }}
              onClick={() => {
                if (isAdmin) {
                  deleteLibraryQuestion(row.original);
                }
              }}
              className={` ${
                isAdmin ? 'me-2 me-md-5 action-icons' : 'disabled me-2 me-md-5'
              }`}
              data-tooltip-id={`delete-${row.original.id}`}
            >
              <Icon
                icon={'trash_01'}
                className={`trash`}
                height={20}
                width={20}
                stroke={
                  isAdmin
                    ? colorState.icon.default.primary
                    : colorState.icon.default.disabled
                }
              >
                {STRINGS.DELETE}
              </Icon>

              <TooltipComponent id={`delete-${row.original.id}`}>
                {STRINGS.DELETE}
              </TooltipComponent>
            </div>
          )}
          {getPermission(currentUser, 'change_field') && (
            <div
              role="button"
              data-testid="edit-question-action"
              aria-label={`edit question ${row.original.display_name}`}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  if (isAdmin) {
                    editQuestion(row.original);
                  }
                }
              }}
              onClick={() => {
                if (isAdmin) {
                  editQuestion(row.original);
                }
              }}
              className={'action-icons'}
              data-tooltip-id={`edit-${row.original.id}`}
            >
              <Icon
                icon={'edit_02'}
                height={20}
                width={20}
                stroke={
                  isAdmin
                    ? colorState.icon.default.primary
                    : colorState.icon.default.disabled
                }
              >
                {STRINGS.EDIT}
              </Icon>
              <TooltipComponent id={`edit-${row.original.id}`}>
                {STRINGS.EDIT}
              </TooltipComponent>
            </div>
          )}
        </section>
      )
    }
  ];

  useEffect(() => {
    if (selectedCategory && getPermission(currentUser, 'view_field')) {
      dispatch(
        requestQuestionsLibraryData({
          categoryId: selectedCategory.id,
          limit: selectedClosure ? 'all' : questionsLibraryTableRowLimit,
          offset: 0,
          ordering: getOrderingParam(tableSortOrder)
        })
      );
      if (selectedRows && Object.keys(selectedRows).length) {
        setSelectedRows({});
        setIsSelectAll(false);
        dispatch(unSelectSelectedRows({ shouldUnselect: true }));
      }
    } else if (categories && !categories?.length) {
      setIsLoadingInitialData(false);
    }
  }, [
    dispatch,
    selectedCategory,
    currentUser,
    categories,
    selectedClosure,
    tableSortOrder
  ]);

  useEffect(() => {
    if (questionslibraryData?.results && isSelectAll) {
      handleSelectAllClick();
    }
  }, [questionslibraryData]);

  useEffect(() => {
    if (!isFetchingQuestionsLibraryData) {
      dispatch(setShowTableLoader({ showTableLoader: false }));
      setIsLoadingInitialData(false);
    }
  }, [tableSortOrder, isFetchingQuestionsLibraryData]);

  const getNextSetOfData = (offset: number) => {
    dispatch(
      requestQuestionsLibraryData({
        offset,
        categoryId: selectedCategory.id,
        limit: questionsLibraryTableRowLimit,
        shouldAppend: true,
        ordering: getOrderingParam(tableSortOrder)
      })
    );
  };

  const submit = () => {
    const params = {
      categoryId: deleteData.category,
      questionId: deleteData.id
    };
    dispatch(
      deleteQuestion({
        params,
        activeTab,
        selectedCategory: selectedCategory?.id,
        limit: selectedClosure ? 'all' : questionsLibraryTableRowLimit
      })
    );
    setIsDeleteModalOpen(false);
  };

  const customConfirmationStyles = {
    content: {
      minWidth: '20%',
      minHeight: '10vh'
    },
    overlay: {
      backgroundColor: 'rgba(114, 114, 114, 0.294)'
    }
  };

  const postModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const renderConfirmationFooter = () => {
    return (
      <div className="gap-3 d-flex">
        <Button
          onClick={() => setIsDeleteModalOpen(false)}
          {...btnStyles.default}
        >
          {STRINGS.CANCEL}
        </Button>
        <Button onClick={submit} {...btnStyles.danger}>
          {STRINGS.YES}
        </Button>
      </div>
    );
  };

  const handleSelectAllClick = () => {
    if (
      selectedRows &&
      Object.keys(selectedRows).length &&
      Object.keys(selectedRows).length === questionslibraryData?.results?.length
    ) {
      setSelectedRows({});
      setIsSelectAll(false);
    } else {
      const data = {};
      questionslibraryData?.results?.forEach((user: any, index: number) => {
        data[index] = true;
      });
      setSelectedRows(data);
      setIsSelectAll(true);
    }
  };

  const handleSelectCheckBoxClick = (index: number) => {
    const data = { ...selectedRows };
    if (selectedRows && selectedRows[index]) {
      delete data[index];
    } else {
      data[index] = true;
    }
    setSelectedRows(data);
    setIsSelectAll(
      data && Object.keys(data) === questionslibraryData?.results?.length
    );
  };

  const NoQuestionsData = () => {
    return (
      <NoDataFound
        src={EmptyQuestionsListIcon}
        description={STRINGS.YOUR_LIST_OF_QUESTIONS_ARE_EMPTY}
        alt={'no-questions-img'}
        isAbsolute
        subMsgDescription={
          STRINGS.YOUR_LIST_OF_QUESTIONS_ARE_EMPTY_BECAUSE_YOU_HAVENT_ADDED_ANY_QUESTIONS_YET
        }
        btn={
          getPermission(currentUser, 'add_field') && (
            <Button {...btnStyles.primary} onClick={() => addQuestion(true)}>
              <Icon
                icon={'add'}
                stroke={colorState.icon.default['primary-inverse']}
              />
              {STRINGS.ADD_NEW_QUESTION}
            </Button>
          )
        }
      />
    );
  };

  const NoCategoryDataScreen = () => {
    return (
      <NoDataFound
        src={EmptyQuestionsListIcon}
        description={STRINGS.YOUR_CATEGORY_LIST_ARE_EMPTY}
        alt={'no-questions-img'}
        isAbsolute
        subMsgDescription="Please add a new category and then add questions to it."
      />
    );
  };

  useEffect(() => {
    setIsLoadingInitialData(true);
  }, []);

  return (
    <div
      data-testid="questions-library-table"
      className="w-100 d-flex overflow-auto flex-grow-1 flex-column"
    >
      {isLoadingInitialData ? (
        <PALoader />
      ) : (
        <Suspense fallback={<PALoader />}>
          <GenericTableRT7
            columns={columns}
            shouldFocusColumnOfRow={true}
            tableData={questionslibraryData?.results || []}
            setSelectedRows={setSelectedRows}
            serialNumber
            showCheckBoxColumn={
              !isQuestionLibrary &&
              getPermission(currentUser, 'add_new_closure_question')
            }
            checkBoxColumnText={'S. No'}
            infiniteScrollSettings={
              !selectedClosure
                ? {
                    getNextSetOfData,
                    pageSize: questionsLibraryTableRowLimit,
                    totalCount: questionslibraryData?.meta?.count
                  }
                : null
            }
            isQuestionsLibrary={true}
            selectedRows={selectedRows}
            noDataFoundScreen={
              !questionslibraryData ||
              !questionslibraryData?.results?.length ? (
                <NoQuestionsData />
              ) : isFetchingQuestionsLibraryData || isLoadingInitialData ? (
                <></>
              ) : categories?.length === 0 ? (
                <NoCategoryDataScreen />
              ) : (
                <></>
              )
            }
            customSelectAllCheckBoxLogic={handleSelectAllClick}
            customSingleCheckBoxLogic={handleSelectCheckBoxClick}
            setSelectedSortOrder={setTableSortOrder}
            defaultSorted={tableSortOrder}
          />
        </Suspense>
      )}
      {isDeleteModalOpen && (
        <ConfirmationPopupModal
          isOpen={isDeleteModalOpen}
          showHeader={true}
          title={STRINGS.DELETE}
          description={STRINGS.ARE_YOU_SURE_WANT_TO_DELETE_QUESTION}
          showFooter={renderConfirmationFooter()}
          style={customConfirmationStyles}
          closeModal={postModalClose}
          showBorderTop={false}
          isBodyPaddingNone={true}
          customFooter={true}
          {...confirmPopupModalStyles}
        />
      )}
    </div>
  );
};
